/**
 * "Export" on each section and chart of the interactive Leadership report
 * (Carl, via Rusty, 28 Oct): for demos and for reporting to users.
 *
 * - "Image (PNG)": a picture of the section or chart as it is on screen, at
 *   twice the screen's pixels, for slides. The controls the printed page
 *   hides (`data-print-hide`: ⓘ, "Data table", search boxes, "Show details",
 *   this menu) are left out, and a footer line says which range and filters
 *   the numbers are for. Drawn in the browser (the element, its computed
 *   styles and the app's fonts in an SVG `foreignObject`, onto a canvas):
 *   nothing leaves the page.
 * - "Data (Excel)" on a section: the server's workbook with only that
 *   section's sheets, for the range and filters on screen.
 * - "Data (CSV)" on a chart: the chart's "Data table".
 *
 * Privacy is the page's own: a picture shows what the section shows, the
 * data is what the section's sheets carry (requesters stay pseudonymous).
 */

import { ChevronDown, Download, Icon } from "@norican/icons";
import { Button, Menu } from "@norican/react";
import { createContext, type RefObject, useContext, useState } from "react";

import { useT } from "@/i18n";
import { exportReportInsights as sdkExportReportInsights } from "@/lib/api";
import { logger } from "@/lib/logger";

import {
  alwaysWritten,
  exportFilename,
  type ExportSection,
  keepsStyle,
  slug,
  tableCsv,
} from "./export-helpers";
import { saveBlob } from "./insights-api";
import { apiQuery, type InsightsState } from "./insights-helpers";
import { HELPDESK_OVERVIEW } from "./report-helpers";

export interface ExportScope {
  state: InsightsState;
  /** The range on screen, for file names. */
  first: string | null;
  last: string | null;
  /** The footer line of a picture ("IT HelpDesk · UAT · 1 Oct – 28 Oct · filters: none"). */
  footer: string;
}

const ExportContext = createContext<ExportScope | null>(null);
export const ExportProvider = ExportContext.Provider;

// ── The picture ─────────────────────────────────────────────────────────────

const SVG_NS = "http://www.w3.org/2000/svg";
/** Twice the screen's pixels: sharp on a slide. */
const SCALE = 2;
let fontCss: Promise<string> | null = null;

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(blob);
  });
}

/** The app's `@font-face` rules with their files inlined (an SVG picture loads nothing). */
function embeddedFonts(): Promise<string> {
  fontCss ??= (async () => {
    const rules: CSSFontFaceRule[] = [];
    for (const sheet of Array.from(document.styleSheets)) {
      try {
        for (const rule of Array.from(sheet.cssRules)) {
          if (rule instanceof CSSFontFaceRule) {
            rules.push(rule);
          }
        }
      } catch {
        // A cross-origin sheet: its rules cannot be read (the app serves its own).
      }
    }
    const parts = await Promise.all(
      rules.map(async (rule) => {
        let css = rule.cssText;
        const base = rule.parentStyleSheet?.href ?? document.baseURI;
        for (const match of css.matchAll(/url\((['"]?)([^'")]+)\1\)/g)) {
          const raw = match[2] ?? "";
          if (raw.startsWith("data:")) {
            continue;
          }
          try {
            const response = await fetch(new URL(raw, base), { credentials: "same-origin" });
            const data = await blobToDataUrl(await response.blob());
            css = css.replace(match[0], `url("${data}")`);
          } catch (error) {
            logger.debug("[leadership-report] font not embedded", raw, error);
          }
        }
        return css;
      }),
    );
    return parts.join("\n");
  })();
  return fontCss;
}

/**
 * The browser's own default styles per element, measured in a shadow root
 * (the app's stylesheets do not reach it), as the SVG picture renders with
 * those defaults only.
 */
class Defaults {
  private readonly root: ShadowRoot;
  private readonly cache = new Map<string, Map<string, string>>();
  constructor(private readonly host: HTMLElement) {
    this.root = host.attachShadow({ mode: "open" });
  }
  of(element: Element): Map<string, string> {
    const key = `${element.namespaceURI ?? ""}|${element.localName}`;
    let values = this.cache.get(key);
    if (!values) {
      const probe = document.createElementNS(element.namespaceURI, element.localName);
      this.root.appendChild(probe);
      const computed = window.getComputedStyle(probe);
      values = new Map();
      for (let i = 0; i < computed.length; i++) {
        const property = computed.item(i);
        values.set(property, computed.getPropertyValue(property));
      }
      this.root.removeChild(probe);
      this.cache.set(key, values);
    }
    return values;
  }
}

/**
 * A copy of `source` with its computed styles written onto it; print-hidden
 * parts left out. A property is written when it differs from the browser's
 * default or from the parent's value (so inheritance comes out right), which
 * keeps the picture small.
 */
function styledCopy(
  source: Element,
  defaults: Defaults,
  parent: CSSStyleDeclaration | null,
): Element | null {
  if (source instanceof HTMLElement && source.dataset.printHide !== undefined) {
    return null;
  }
  const copy = source.cloneNode(false) as Element;
  const isSvg = source instanceof SVGElement;
  let computed: CSSStyleDeclaration | null = null;
  if (source instanceof HTMLElement || isSvg) {
    computed = window.getComputedStyle(source);
    if (computed.display === "none") {
      return null;
    }
    const base = defaults.of(source);
    const empty = source.children.length === 0 && !(source.textContent ?? "").trim();
    const fixedHeight = isSvg || empty;
    const declarations: string[] = [];
    for (let i = 0; i < computed.length; i++) {
      const property = computed.item(i);
      if (!keepsStyle(property, fixedHeight)) {
        continue;
      }
      const value = computed.getPropertyValue(property);
      if (
        alwaysWritten(property) ||
        value !== base.get(property) ||
        (parent && value !== parent.getPropertyValue(property))
      ) {
        declarations.push(`${property}:${value}`);
      }
    }
    copy.setAttribute("style", declarations.join(";"));
    copy.removeAttribute("class");
  }
  for (const child of Array.from(source.childNodes)) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const inner = styledCopy(child as Element, defaults, computed ?? parent);
      if (inner) {
        copy.appendChild(inner);
      }
    } else if (child.nodeType === Node.TEXT_NODE) {
      copy.appendChild(child.cloneNode(false));
    }
  }
  return copy;
}

/** The element as a PNG, with `footer` under it. */
export async function elementPng(element: HTMLElement, footer: string): Promise<Blob> {
  const width = Math.ceil(element.getBoundingClientRect().width);
  const fullWidth = width + 32;
  const page = window.getComputedStyle(document.body);
  // Off screen: the defaults are measured, and the copy laid out, in its shadow root.
  const holder = document.createElement("div");
  holder.setAttribute(
    "style",
    "all:initial;position:fixed;left:-100000px;top:0;pointer-events:none;opacity:0",
  );
  document.body.appendChild(holder);
  try {
    const defaults = new Defaults(holder);
    const frame = document.createElement("div");
    const background =
      page.backgroundColor && page.backgroundColor !== "rgba(0, 0, 0, 0)"
        ? page.backgroundColor
        : "#ffffff";
    frame.setAttribute(
      "style",
      `display:block;width:${fullWidth}px;padding:16px;box-sizing:border-box;` +
        `background:${background};font-family:${page.fontFamily};color:${page.color}`,
    );
    const copy = styledCopy(element, defaults, null);
    if (copy) {
      frame.appendChild(copy);
    }
    if (footer) {
      const line = document.createElement("div");
      line.setAttribute(
        "style",
        "margin-top:8px;font-size:11px;line-height:1.4;opacity:0.65;text-align:end",
      );
      line.textContent = footer;
      frame.appendChild(line);
    }
    // Laid out as the picture will be: no app stylesheets, only what is written on it.
    const shadow = holder.shadowRoot;
    shadow?.appendChild(frame);
    const height = Math.ceil(frame.getBoundingClientRect().height);
    shadow?.removeChild(frame);
    return await draw(frame, fullWidth, height);
  } finally {
    document.body.removeChild(holder);
  }
}

async function draw(frame: HTMLElement, fullWidth: number, height: number): Promise<Blob> {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("width", String(fullWidth * SCALE));
  svg.setAttribute("height", String(height * SCALE));
  svg.setAttribute("viewBox", `0 0 ${fullWidth} ${height}`);
  const style = document.createElementNS(SVG_NS, "style");
  style.textContent = await embeddedFonts();
  svg.appendChild(style);
  const object = document.createElementNS(SVG_NS, "foreignObject");
  object.setAttribute("width", "100%");
  object.setAttribute("height", "100%");
  object.appendChild(frame);
  svg.appendChild(object);
  const markup = new XMLSerializer().serializeToString(svg);

  const image = new Image();
  image.decoding = "sync";
  const loaded = new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error("the picture could not be drawn"));
  });
  image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
  await loaded;
  const canvas = document.createElement("canvas");
  canvas.width = fullWidth * SCALE;
  canvas.height = height * SCALE;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("no canvas");
  }
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("no PNG"))), "image/png"),
  );
}

// ── The menu ────────────────────────────────────────────────────────────────

async function downloadSectionExcel(scope: ExportScope, section: ExportSection): Promise<void> {
  const { data, response } = await sdkExportReportInsights({
    path: { name: HELPDESK_OVERVIEW },
    query: { ...apiQuery(scope.state), section },
    parseAs: "blob",
    throwOnError: true,
  });
  const disposition = response.headers.get("content-disposition") ?? "";
  const filename =
    /filename="([^"]+)"/.exec(disposition)?.[1] ?? `helpdesk-leadership-${section}.xlsx`;
  saveBlob(data as unknown as Blob, filename);
}

/**
 * The small "Export" menu of a section (`section`: picture + its Excel sheets)
 * or a chart (`table`: picture + its numbers as CSV).
 */
export function ExportMenu({
  title,
  target,
  section,
  table,
}: {
  title: string;
  target: RefObject<HTMLElement | null>;
  section?: ExportSection;
  table?: { columns: readonly string[]; rows: readonly unknown[][] };
}) {
  const { t } = useT("common");
  const scope = useContext(ExportContext);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  if (!scope) {
    return null;
  }
  const range = { first: scope.first, last: scope.last };
  const label = t("leadershipReport.live.export.menuOf", { name: title });

  async function run(what: string, job: () => Promise<void>) {
    setBusy(true);
    setFailed(false);
    try {
      await job();
      logger.debug("[leadership-report] exported", what, title);
    } catch (error) {
      logger.warn("[leadership-report] export failed", what, title, error);
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  const png = () =>
    run("png", async () => {
      const element = target.current;
      if (!element) {
        throw new Error("nothing to draw");
      }
      saveBlob(await elementPng(element, scope.footer), exportFilename(title, range, "png"));
    });

  return (
    <span className="inline-flex items-center gap-1.5" data-print-hide>
      {failed ? (
        <span className="text-danger-fg text-[12px]" role="status">
          {t("leadershipReport.live.export.failed")}
        </span>
      ) : null}
      <Menu.Root>
        <Menu.Trigger
          render={
            <Button
              size="sm"
              variant="ghost"
              tone="neutral"
              startIcon={<Icon icon={Download} size="sm" />}
              endIcon={<Icon icon={ChevronDown} size="sm" />}
              aria-label={label}
              title={label}
              disabled={busy}
            />
          }
        >
          <span className="text-[12px]">
            {busy ? t("leadershipReport.live.export.busy") : t("leadershipReport.live.export.menu")}
          </span>
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Positioner sideOffset={4} align="end">
            <Menu.Popup width="content">
              <Menu.Item onClick={() => void png()}>
                {t("leadershipReport.live.export.png")}
              </Menu.Item>
              {section ? (
                <Menu.Item
                  onClick={() => void run("excel", () => downloadSectionExcel(scope, section))}
                >
                  {t("leadershipReport.live.export.excel")}
                </Menu.Item>
              ) : null}
              {table ? (
                <Menu.Item
                  onClick={() =>
                    void run("csv", async () =>
                      saveBlob(
                        new Blob([tableCsv(table)], { type: "text/csv;charset=utf-8" }),
                        exportFilename(slug(title), range, "csv"),
                      ),
                    )
                  }
                >
                  {t("leadershipReport.live.export.csv")}
                </Menu.Item>
              ) : null}
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>
    </span>
  );
}
